import { Dialog, DialogContent, Divider, Typography } from "@mui/material";
import { Button, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { VerticalLine, TabButton } from "./styles";

import DialogActions from "../../../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../../../containers/LoadingOverlay/LoadingOverlay";
import SignaturePadWrapper from "../../../../../../containers/SignaturePadContainer/SignaturePadWrapper";
import { useAppSelector } from "../../../../../../custom-hooks/redux/redux";
import { EmployeeDTO } from "../../../../../../models/employees";

import { createSVGFileFromText } from "../../../../../../utils/files-utils/create-svg-file-from-text";

import { convertToBold } from "../../../../../../utils/styles-utils/bold-in-translated-txt";

interface Props {
  disabled?: boolean;
  employee?: EmployeeDTO;
  openFromTheStart?: boolean;
  handleSubmit: (signature: string) => void;
  children?: React.ReactNode;
  translationRootPath: string;
  parentIsLoading: boolean;
}
interface State {
  signoffMethod: "signature" | "name" | "eData";
  loading: boolean;
  error: string;
}

const SubmitOptions = ({
  disabled,
  employee,
  openFromTheStart = false,
  handleSubmit,
  children,
  translationRootPath,
  parentIsLoading,
}: Props) => {
  const { t } = useTranslation();

  const tRP = `${translationRootPath}`;

  const theme = useTheme();
  const [open, setOpen] = useState(openFromTheStart);

  const [state, setState] = useState<State>({
    signoffMethod: "signature",
    loading: false,
    error: "",
  });

  const { currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const handleSignature = (file: File) => {
    const reader = new FileReader();

    reader.onloadend = function () {
      const base64String = (reader.result as string)?.split(",")[1]; // Extract the Base64 content

      handleSubmit(base64String);
    };

    // Read the file as a Data URL, which will be base64-encoded
    reader.readAsDataURL(file);
  };

  const handleSignOffByName = async () => {
    const _file = createSVGFileFromText(
      [
        {
          text: currentUserData?.user?.name ?? employee?.name ?? "MISSING_USER_NAME",
          color: "rgb(66, 133, 244)",
          fontSize: "20px",
          fontWeight: "normal",
          fontStyle: "italic",
        },
      ],
      `sig_${uuid()}.svg`,
      "0 0 200 1",
      "6000",
      "2000"
    );

    handleSignature(_file);
  };

  const handleSignOffByEData = async () => {
    const _file = createSVGFileFromText(
      [
        {
          text: "Electronic trace",
          color: "rgb(66, 133, 244)",
          fontSize: "20px",
          fontWeight: "normal",
          fontStyle: "italic",
        },
      ],
      `sig_${uuid()}.svg`,
      "0 0 200 1",
      "6000",
      "2000"
    );

    handleSignature(_file);
  };

  return (
    <>
      {children ? (
        <Grid onClick={() => !disabled && setOpen(true)}>{children}</Grid>
      ) : (
        <Button
          onClick={() => setOpen(true)}
          variant="contained"
          disabled={!!disabled}
        >
          {`${t(`${tRP}.open_button`)}`}
        </Button>
      )}

      <Dialog open={open}>
        <DialogTitle
          handleClose={() => setOpen(false)}
          title={`${t(`${tRP}.title`)}`}
        />
        <LoadingOverlay
          loading={state.loading || parentIsLoading}
          error={state.error}
        >
          <DialogContent>
            <Grid
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
                gap: "10px",
              }}
            >
              <TabButton
                variant={state.signoffMethod === "signature" ? "outlined" : "contained"}
                onClick={() => setState((state) => ({ ...state, signoffMethod: "signature" }))}
              >
                {`${t(`${tRP}.submit_by_signature`)}`}
                {state.signoffMethod === "signature" && <VerticalLine orientation="vertical" />}
              </TabButton>
              <TabButton
                variant={state.signoffMethod === "name" ? "outlined" : "contained"}
                onClick={() => setState((state) => ({ ...state, signoffMethod: "name" }))}
              >
                {`${t(`${tRP}.submit_by_name`)}`}
                {state.signoffMethod === "name" && <VerticalLine orientation="vertical" />}
              </TabButton>
              <TabButton
                variant={state.signoffMethod === "eData" ? "outlined" : "contained"}
                onClick={() => setState((state) => ({ ...state, signoffMethod: "eData" }))}
              >
                {`${t(`${tRP}.submit_by_edata`)}`}
                {state.signoffMethod === "eData" && <VerticalLine orientation="vertical" />}
              </TabButton>
            </Grid>

            <Divider
              style={{ borderColor: theme.palette.primary.main, borderWidth: "1.5px" }}
              orientation="horizontal"
            />

            {state.signoffMethod === "signature" && (
              <SignaturePadWrapper
                title={`${t(`${tRP}.message_to_submit`)}`}
                text={`${t(`${tRP}.confirmation_message`)}`}
                buttonTitle="Sign and submit"
                handleSignature={handleSignature}
                handleCancel={() => setOpen(false)}
                alertWhenEmpty="Please draw your signature to submit"
              />
            )}

            {state.signoffMethod === "name" && (
              <Grid style={{ minHeight: "30vh" }}>
                <Typography style={{ marginTop: "15px" }}>
                  {convertToBold(`${t(`${tRP}.submit_by_name_confirm`)}`)}
                </Typography>
              </Grid>
            )}

            {state.signoffMethod === "eData" && (
              <Grid style={{ minHeight: "30vh" }}>
                <Typography style={{ marginTop: "15px" }}>
                  {convertToBold(`${t(`${tRP}.submit_by_edata_confirm`)}`)}
                </Typography>
              </Grid>
            )}
          </DialogContent>

          {(state.signoffMethod === "name" || state.signoffMethod === "eData") && (
            <DialogActions
              handleCancel={() => setOpen(false)}
              handleSave={state.signoffMethod === "name" ? handleSignOffByName : handleSignOffByEData}
              customSaveName={`${t("common_words.yes")}`}
            />
          )}
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default SubmitOptions;
